import { useState, useCallback } from 'react';
import { Recipe } from '../types';
import { dataService } from '@foodbook/core-logic';

interface UseRecipesProps {
  initialRecipes: Recipe[];
}

interface UseRecipesReturn {
  // State
  recipes: Recipe[];
  setRecipes: (recipes: Recipe[] | ((prev: Recipe[]) => Recipe[])) => void;

  // Handlers
  handleToggleFavorite: (recipeId: string, favoriteRecipeIds: string[], setFavoriteRecipeIds: (ids: string[]) => void) => Promise<void>;
  handleRecipeSelect: (recipe: Recipe, addDishTargetMeal: string | null, handleAddDishConfirm: (recipe: Recipe) => void, navigate: (view: string, params?: any) => void) => void;
}

export const useRecipes = ({ initialRecipes }: UseRecipesProps): UseRecipesReturn => {
  const [recipes, setRecipesInternal] = useState<Recipe[]>(initialRecipes);

  const setRecipes = useCallback((newRecipes: Recipe[] | ((prev: Recipe[]) => Recipe[])) => {
    setRecipesInternal(prev => {
      return typeof newRecipes === 'function' ? newRecipes(prev) : newRecipes;
    });
  }, []);

  // Handle Favorites - with cloud persistence
  const handleToggleFavorite = useCallback(async (recipeId: string, favoriteRecipeIds: string[], setFavoriteRecipeIds: (ids: string[]) => void) => {
    const isFav = favoriteRecipeIds.includes(recipeId);

    // Optimistically update local state
    setRecipes(prev => prev.map(r => {
      if (r.id !== recipeId) return r;
      const currentCount = r.favoriteCount ?? (isFav ? 1 : 0);
      return {
        ...r,
        isFavorite: !isFav,
        favoriteCount: isFav ? Math.max(0, currentCount - 1) : currentCount + 1,
      };
    }));
    setFavoriteRecipeIds(
      isFav
        ? favoriteRecipeIds.filter(id => id !== recipeId)
        : [...favoriteRecipeIds, recipeId]
    );

    // Persist to cloud
    try {
      await dataService.toggleFavorite('recipe', recipeId);
    } catch (error) {
      console.error('[useRecipes] Failed to sync favorite to cloud:', error);
      // Revert on error
      setRecipes(prev => prev.map(r => {
        if (r.id !== recipeId) return r;
        const nextCount = r.favoriteCount ?? (!isFav ? 1 : 0);
        return {
          ...r,
          isFavorite: isFav,
          favoriteCount: isFav ? nextCount + 1 : Math.max(0, nextCount - 1),
        };
      }));
      setFavoriteRecipeIds(
        isFav
          ? [...favoriteRecipeIds, recipeId]
          : favoriteRecipeIds.filter(id => id !== recipeId)
      );
    }
  }, [setRecipes]);

  // Handle Recipe Selection
  const handleRecipeSelect = useCallback((recipe: Recipe, addDishTargetMeal: string | null, handleAddDishConfirm: (recipe: Recipe) => void, navigate: ((view: string, params?: any) => void) | ((path: string) => void)) => {
    if (addDishTargetMeal) {
      handleAddDishConfirm(recipe);
    } else {
      // Support both old navigation and react-router navigate
      if (typeof navigate === 'function') {
        // Check if it's react-router navigate (accepts string path)
        navigate(`/recipe/${recipe.id}`);
      } else {
        // Old navigation style
        (navigate as any)('recipe-detail', { recipeId: recipe.id });
      }
    }
  }, []);

  return {
    recipes,
    setRecipes,
    handleToggleFavorite,
    handleRecipeSelect,
  };
};
